import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './text-field.module.scss';

export type TextFieldProps = Omit<ComponentPropsWithRef<'input'>, 'size'> & {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** "underline" — checkout/account fields; "box" — auth and size advisor fields. */
  appearance?: 'underline' | 'box';
  /** "caps" — small uppercase label; "plain" — 13px medium label. */
  labelStyle?: 'caps' | 'plain';
  suffix?: ReactNode;
  adornment?: ReactNode;
  labelAside?: ReactNode;
  inputClassName?: string | undefined;
};

export function TextField({
  id,
  label,
  hint,
  error,
  className,
  inputClassName,
  appearance = 'underline',
  labelStyle = 'caps',
  suffix,
  adornment,
  labelAside,
  'aria-describedby': describedBy,
  ...props
}: TextFieldProps) {
  const description =
    [describedBy, hint ? `${id}-hint` : null, error ? `${id}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;
  const filled = typeof props.value === 'string' ? props.value.length > 0 : undefined;
  return (
    <div className={cx(styles.root, className)} data-appearance={appearance}>
      <div className={styles.head}>
        <label className={styles.label} htmlFor={id} data-style={labelStyle}>
          {label}
        </label>
        {labelAside}
      </div>
      <div
        className={styles.control}
        data-invalid={Boolean(error) || undefined}
        data-filled={filled || undefined}
      >
        <input
          {...props}
          id={id}
          className={cx(styles.input, inputClassName)}
          aria-describedby={description}
          aria-invalid={Boolean(error) || props['aria-invalid']}
        />
        {suffix && <span className={styles.suffix}>{suffix}</span>}
        {adornment && (
          <span className={styles.adornment} aria-hidden="true">
            {adornment}
          </span>
        )}
      </div>
      {hint && (
        <p className={styles.hint} id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className={styles.error} role="alert" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
